import { Task, UnassignedFilterOptions } from '@bringg/types';

import { fieldValueGetter } from 'bringg-web/features/monitoring/shared/realtime-updates/updates/field-value-getter';
import { TaskFiltersObject } from '../components/monitoring-filter-bar/types';

type GetTask = <T>(field: string, innerField?: string) => T;

type FilterActions = Record<keyof TaskFiltersObject, (filters: TaskFiltersObject, task: GetTask) => boolean>;

const filterActions: FilterActions = {
	selectedTeams: (filters: TaskFiltersObject, task: GetTask) =>
		filters.selectedTeams?.includes(task<number[]>('team_ids')[0]),
	selectedUsers: (filters: TaskFiltersObject, task: GetTask) =>
		filters.selectedUsers.includes(task<number>('user_id')),
	selectedRuns: (filters: TaskFiltersObject, task: GetTask) => filters.selectedRuns.includes(task<number>('run_id')),
	selectedTasks: (filters: TaskFiltersObject, task: GetTask) => filters.selectedTasks.includes(task<number>('id')),
	unassigned: (filters: TaskFiltersObject, task: GetTask) => {
		const isAssigned = !!task<number>('user_id');

		return filters.unassigned === UnassignedFilterOptions.ASSIGNED_TASKS ? isAssigned : !isAssigned;
	},
	sla: (filters: TaskFiltersObject, task: GetTask) => {
		const firstAttemptPromiseNoEarlierThan = task<string>('lastWayPoint.first_attempt_promise_no_earlier_than');
		const firstAttemptPromiseNoLaterThan = task<string>('lastWayPoint.first_attempt_promise_no_later_than');

		return checkDateRange(firstAttemptPromiseNoEarlierThan, firstAttemptPromiseNoLaterThan, filters.sla);
	},
	fleets: (filters: TaskFiltersObject, task: GetTask) => {
		const fleetId = task<number>('fleet_id');

		return filters.fleets.some(fleet => {
			if (fleet === -1 && !fleetId) {
				return true;
			}
			return fleetId === fleet;
		});
	},
	ranks: (filters: TaskFiltersObject, task: GetTask) => {
		const taskRank = task<number>('rank');
		return taskRank && filters.ranks.some(rank => taskRank === rank);
	},
	unscheduled: (filters: TaskFiltersObject, task: GetTask) => {
		return task<boolean>('virtual_task');
	},
	selectedTags: (filters: TaskFiltersObject, task: GetTask) => {
		const tagId = task<number>('tag_id');
		return filters.selectedTags.includes(tagId);
	},
	servicePlans: (filters: TaskFiltersObject, task: GetTask) => {
		const servicePlanId = task<number>('service_plan_id');

		return (
			servicePlanId &&
			filters.servicePlans.some(servicePlan => {
				return servicePlanId === servicePlan;
			})
		);
	},
	timeWindow: (filters: TaskFiltersObject, task: GetTask) => {
		const taskStartTimeWindow = task<string>('lastWayPoint.no_earlier_than');
		const taskEndTimeWindow = task<string>('lastWayPoint.no_later_than');
		const scheduledAt = task<string>('lastWayPoint.scheduled_at');

		if (taskEndTimeWindow || taskStartTimeWindow) {
			return checkDateRange(taskStartTimeWindow, taskEndTimeWindow, filters.timeWindow);
		}

		if (scheduledAt) {
			return checkDateRange(scheduledAt, scheduledAt, filters.timeWindow);
		}
	},
	reservedUntil: (filters: TaskFiltersObject, task: GetTask) => {
		const reservedUntil = task<string>('reserved_until');
		return checkDateRange(reservedUntil, reservedUntil, filters.reservedUntil);
	},
	skills: (filters: TaskFiltersObject, task: GetTask) => {
		const requiredSkills = task<string[]>('required_skills');

		return (
			requiredSkills &&
			filters.skills.some(skill => {
				return requiredSkills.includes(skill);
			})
		);
	}
};

function filter(filters: TaskFiltersObject, task: Task, fallback: Task) {
	return Object.keys(filters || {}).every(filterKey => {
		return (
			!filterActions[filterKey] ||
			filterActions[filterKey](filters, (path: string) => fieldValueGetter.get(task, fallback, path))
		);
	});
}

function checkDateRange(start: string, end: string, filterRange: [string, string]) {
	if (!start && !end) {
		return false;
	}

	let isInRange = true;

	if (start && filterRange[0]) {
		isInRange = isInRange && start >= filterRange[0];
	}

	if (end && filterRange[1]) {
		isInRange = isInRange && end <= filterRange[1];
	}

	return isInRange;
}

export const tasksFilterer = { filter };
